import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useReportPermissions } from '../../../hooks/useReportPermissions';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  AlertTitle,
  Grid
} from '@mui/material';
import {
  FileDownload as FileDownloadIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import { getWarehouses } from '../../../services/productService';
import { getProductInventoryTransactions } from '../../../services/inventoryService';
import InventoryMovementFilter from './InventoryMovementFilter';
import InventoryMovementTable from './InventoryMovementTable';
import { InventoryTransaction } from '../../../types/inventory';

// واجهة بيانات الفلتر
interface FilterData {
  barcode: string;
  productName: string;
  variantName: string;
  warehouse: string;
  dateFrom: string;
  dateTo: string;
}

const InventoryMovement: React.FC = () => {
  // استخدام هوك التنقل والصلاحيات
  const navigate = useNavigate();
  const { hasReportPagePermission, hasReportExportPermission } = useReportPermissions();

  // حالات المكون
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [movementData, setMovementData] = useState<InventoryTransaction[]>([]);
  const [filteredData, setFilteredData] = useState<InventoryTransaction[]>([]);
  const [warehouses, setWarehouses] = useState<string[]>([]);
  const [warehouseIds, setWarehouseIds] = useState<{[key: string]: string}>({});
  const [filterValues, setFilterValues] = useState<FilterData>({
    barcode: '',
    productName: '',
    variantName: '',
    warehouse: '',
    dateFrom: '',
    dateTo: ''
  });

  // جلب البيانات من الخادم
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // جلب قائمة المستودعات مع تمرير السياق
        const warehousesResponse = await getWarehouses(1, 100, undefined, 'inventory-movement');
        
        if (!warehousesResponse.warehouses || !Array.isArray(warehousesResponse.warehouses)) {
          throw new Error('لم يتم العثور على مستودعات');
        }
        
        // تخزين أسماء المستودعات ومعرفاتها
        const warehouseNames = warehousesResponse.warehouses.map(wh => wh.name);
        const warehouseIdMap: {[key: string]: string} = {};
        warehousesResponse.warehouses.forEach(wh => {
          if (wh._id) {
            warehouseIdMap[wh.name] = wh._id;
          }
        });
        
        setWarehouses(warehouseNames);
        setWarehouseIds(warehouseIdMap);
        
        // جلب بيانات حركة المخزون
        try {
          // إعداد معاملات البحث
          const search = filterValues.barcode || filterValues.productName || '';
          const warehouseId = filterValues.warehouse ? warehouseIdMap[filterValues.warehouse] : undefined;
          
          // هنا نحصل على بيانات حركة المخزون لجميع المنتجات
          // استخدام المعاملات الإضافية للبحث
          const response = await getProductInventoryTransactions(
            'all',
            undefined,
            1,
            500000, // زيادة الحد الأقصى لعرض جميع المعاملات
            search,
            filterValues.dateFrom,
            filterValues.dateTo,
            warehouseId
          );
          
          if (response.success) {
            setMovementData(response.transactions || []);
            setFilteredData(response.transactions || []);
          } else {
            console.warn('استجابة حركة المخزون فارغة أو غير صالحة');
            setMovementData([]);
            setFilteredData([]);
          }
        } catch (movementError) {
          console.error('خطأ في جلب بيانات حركة المخزون:', movementError);
          setError('حدث خطأ أثناء تحميل بيانات حركة المخزون');
          setMovementData([]);
          setFilteredData([]);
        }
        
        setLoading(false);
        
      } catch (err) {
        console.error('خطأ في جلب البيانات:', err);
        setError('حدث خطأ أثناء تحميل البيانات. يرجى المحاولة مرة أخرى.');
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // معالجة تغيير الفلتر
  const handleFilterChange = async (newFilterValues: FilterData) => {
    setFilterValues(newFilterValues);
    setLoading(true);
    
    try {
      // إعداد معاملات البحث
      const search = newFilterValues.barcode || newFilterValues.productName || '';
      const warehouseId = newFilterValues.warehouse ? warehouseIds[newFilterValues.warehouse] : undefined;
      
      // جلب البيانات من الخادم مع الفلاتر الجديدة
      const response = await getProductInventoryTransactions(
        'all',
        undefined,
        1,
        500000, // زيادة الحد الأقصى لعرض جميع المعاملات
        search,
        newFilterValues.dateFrom,
        newFilterValues.dateTo,
        warehouseId
      );
      
      if (response.success) {
        setMovementData(response.transactions || []);
        
        // تطبيق الفلاتر المحلية المتبقية (اسم المتغير)
        let filtered = response.transactions || [];
        // فلتر باسم المتغير فقط (الفلاتر الأخرى تم تطبيقها في الخادم)
        if (newFilterValues.variantName) {
          filtered = filtered.filter(item => {
            if (!item.variantAttributes) return false;
            return Object.values(item.variantAttributes).some(
              attr => typeof attr === 'string' && attr.toLowerCase().includes(newFilterValues.variantName.toLowerCase())
            );
          });
        }
        
        setFilteredData(filtered);
      } else {
        console.warn('استجابة حركة المخزون فارغة أو غير صالحة');
        setMovementData([]);
        setFilteredData([]);
      }
    } catch (error) {
      console.error('خطأ في جلب بيانات حركة المخزون:', error);
      setError('حدث خطأ أثناء تحميل بيانات حركة المخزون');
      setMovementData([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  // تصدير البيانات إلى ملف إكسل
  const exportToExcel = async () => {
    // التحقق من صلاحية تصدير الإكسل
    if (!hasReportExportPermission('inventory-movement')) {
      setError('ليس لديك صلاحية لتصدير التقرير');
      return;
    }
    
    try {
      // استيراد مكتبة ExcelJS بشكل ديناميكي
      const ExcelJS = await import('exceljs');
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('حركة المخزون');
      
      // إضافة رأس الجدول
      const headers = [
        'التاريخ',
        'نوع الحركة',
        'اسم المنتج',
        'المتغير',
        'الكمية',
        'الكمية الجديدة',
        'المستودع',
        'باركود المتغير',
        'المرجع',
        'ملاحظات'
      ];
      
      worksheet.addRow(headers);
      
      // تنسيق العناوين
      worksheet.getRow(1).font = { bold: true, size: 12 };
      worksheet.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' };
      
      // وظيفة لتحويل نوع الحركة إلى نص عربي
      const getTransactionTypeText = (type: string): string => {
        switch (type) {
          case 'addition': return 'إضافة';
          case 'subtraction': return 'خصم';
          case 'transfer': return 'تحويل';
          case 'transfer_in': return 'وارد تحويل';
          case 'transfer_out': return 'صادر تحويل';
          case 'adjustment': return 'تعديل';
          default: return type;
        }
      };

      // تحويل خصائص المتغير إلى نص
      const getVariantText = (item: InventoryTransaction): string => {
        if (!item.variantAttributes) return '-';
        return Object.values(item.variantAttributes).join(' - ');
      };
      
      // إضافة البيانات
      filteredData.forEach(item => {
        const date = new Date(item.transactionDate);
        const formattedDate = date.toLocaleDateString('ar-SA');
        
        // تحديد إشارة الكمية بناءً على نوع الحركة
        let quantityWithSign = '';
        if (item.type === 'addition') {
          quantityWithSign = `+${item.quantity} ${item.unitName}`;
        } else if (item.type === 'subtraction') {
          quantityWithSign = `-${item.quantity} ${item.unitName}`;
        } else if (item.type === 'adjustment') {
          // للتعديل، نحسب الفرق
          const diff = item.newQuantity - item.previousQuantity;
          quantityWithSign = `${diff >= 0 ? '+' : ''}${diff} ${item.unitName}`;
        } else {
          // للتحويل
          quantityWithSign = `${item.quantity} ${item.unitName}`;
        }
        
        // استخراج الباركود من المتغير
        const getBarcode = (item: InventoryTransaction): string => {
          // أولاً: التحقق من حقل الباركود المباشر
          if (item.barcode) {
            return item.barcode;
          }
          
          // ثانياً: البحث في خصائص المتغير
          if (item.variantAttributes && typeof item.variantAttributes === 'object') {
            // البحث عن الباركود باللغة الإنجليزية أو العربية
            if (item.variantAttributes.barcode) {
              return item.variantAttributes.barcode;
            }
            if (item.variantAttributes.باركود) {
              return item.variantAttributes.باركود;
            }
            
            // البحث في كل خصائص المتغير
            const barcodeEntry = Object.entries(item.variantAttributes)
              .find(([key]) => key.toLowerCase() === 'barcode' || key === 'باركود');
            
            if (barcodeEntry) {
              return barcodeEntry[1] as string;
            }
          }
          
          return '-';
        };
        
        const row = [
          formattedDate,
          getTransactionTypeText(item.type),
          item.productName,
          getVariantText(item),
          quantityWithSign,
          `${item.newQuantity} ${item.unitName}`,
          item.warehouseName,
          getBarcode(item),
          item.referenceNumber || '-',
          item.notes || '-'
        ];
        
        worksheet.addRow(row);
      });
      
      // تنسيق جميع الخلايا
      worksheet.columns.forEach(column => {
        column.width = 20;
        column.alignment = { vertical: 'middle', horizontal: 'right' };
      });
      
      // تعديل اتجاه الصفحة إلى RTL
      worksheet.views = [
        { rightToLeft: true }
      ];
      
      // تحويل الملف إلى Buffer
      const buffer = await workbook.xlsx.writeBuffer();
      
      // إنشاء Blob من البيانات
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      // إنشاء رابط تحميل
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `تقرير_حركة_المخزون_${new Date().toLocaleDateString('ar-SA')}.xlsx`;
      
      // تنزيل الملف
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (error) {
      console.error('خطأ في تصدير البيانات إلى إكسل:', error);
      alert('حدث خطأ أثناء تصدير البيانات إلى إكسل');
    }
  };

  // عرض شاشة التحميل
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" sx={{ mr: 2 }}>
          جاري تحميل البيانات...
        </Typography>
      </Box>
    );
  }

  // عرض رسالة الخطأ
  if (error) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        <AlertTitle>خطأ</AlertTitle>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* العنوان وزر التصدير */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
          حركة المخزون
        </Typography>
        {hasReportExportPermission('inventory-movement') && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<FileDownloadIcon />}
            onClick={exportToExcel}
            sx={{
              bgcolor: '#30c3b0',
              '&:hover': { bgcolor: '#25a899' },
              boxShadow: '0 4px 12px rgba(48, 195, 176, 0.2)',
            }}
          >
            تصدير إلى إكسل
          </Button>
        )}
      </Box>

      {/* قسم الفلتر */}
      <Paper
        elevation={2}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <FilterListIcon sx={{ color: '#30c3b0', mr: 1 }} />
          <Typography variant="h6" component="h3">
            تصفية البيانات
          </Typography>
        </Box>
        <InventoryMovementFilter
          filterValues={filterValues}
          warehouses={warehouses}
          onFilterChange={handleFilterChange}
        />
      </Paper>

      {/* جدول البيانات */}
      <Paper
        elevation={2}
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
        }}
      >
        <InventoryMovementTable
          data={filteredData}
        />
      </Paper>

      {/* عرض نتائج الفلتر */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          إجمالي النتائج: {filteredData.length} عملية
        </Typography>
      </Box>
    </Box>
  );
};

export default InventoryMovement;